/**
 * MCP Server Card Component
 * Displays individual MCP server info with status and actions
 */

import React, { useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Server, TestTube, Pencil, Trash2, AlertCircle, CheckCircle, Circle } from 'lucide-react';
import type { MCPServer } from '@/types/system';
import { useTestMCPConnection } from '../hooks/useSystemConfig';

interface MCPServerCardProps {
  server: MCPServer;
  onEdit: (server: MCPServer) => void;
  onDelete: (serverId: string) => void;
}

export const MCPServerCard: React.FC<MCPServerCardProps> = ({ server, onEdit, onDelete }) => {
  const [testResult, setTestResult] = useState<{ success: boolean; error?: string } | null>(null);
  const testConnection = useTestMCPConnection();

  const handleTest = async () => {
    setTestResult(null);
    try {
      const result = await testConnection.mutateAsync(server.id);
      setTestResult(result);
    } catch (error) {
      setTestResult({ success: false, error: String(error) });
    }
  };

  const getStatusIcon = () => {
    switch (server.status) {
      case 'connected':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Circle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = () => {
    switch (server.status) {
      case 'connected':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <Card className={`p-4 ${getStatusColor()} transition-colors`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1">
          <Server className="w-5 h-5 text-gray-600 mt-1" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-medium text-gray-900">{server.name}</h4>
              {getStatusIcon()}
              {server.config.status === 0 && (
                <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-700 rounded">
                  Disabled
                </span>
              )}
            </div>

            {server.config.description && (
              <p className="text-sm text-gray-600 mb-2">{server.config.description}</p>
            )}

            <div className="space-y-1 text-xs text-gray-500">
              <div className="flex items-center gap-2">
                <span className="font-medium">Transport:</span>
                <span className="px-2 py-0.5 bg-white rounded border border-gray-200">
                  {server.config.transport}
                </span>
              </div>

              {server.config.transport === 'stdio' && server.config.command && (
                <div className="flex items-start gap-2">
                  <span className="font-medium">Command:</span>
                  <code className="px-2 py-0.5 bg-white rounded border border-gray-200 font-mono">
                    {server.config.command}
                    {server.config.args && ` ${server.config.args.join(' ')}`}
                  </code>
                </div>
              )}

              {(server.config.transport === 'sse' || server.config.transport === 'streamable-http') && server.config.url && (
                <div className="flex items-center gap-2">
                  <span className="font-medium">URL:</span>
                  <code className="px-2 py-0.5 bg-white rounded border border-gray-200 font-mono text-xs break-all">
                    {server.config.url}
                  </code>
                </div>
              )}

              {server.tools_count !== undefined && (
                <div className="flex items-center gap-2">
                  <span className="font-medium">Tools:</span>
                  <span className="px-2 py-0.5 bg-white rounded border border-gray-200">
                    {server.tools_count}
                  </span>
                </div>
              )}

              {server.status === 'connected' && (
                <div className="text-green-600 font-medium">Connected</div>
              )}

              {server.status === 'error' && (
                <div className="text-red-600 font-medium">Connection Failed</div>
              )}
            </div>

            {testResult && (
              <div
                className={`mt-3 p-2 rounded text-xs ${
                  testResult.success
                    ? 'bg-green-100 text-green-800 border border-green-200'
                    : 'bg-red-100 text-red-800 border border-red-200'
                }`}
              >
                {testResult.success ? (
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>Connection test successful</span>
                  </div>
                ) : (
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5" />
                    <span>{testResult.error || 'Connection test failed'}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 ml-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleTest}
            disabled={testConnection.isPending}
            title="Test connection"
          >
            <TestTube className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(server)}
            title="Edit server"
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (confirm(`Delete MCP server "${server.name}"?`)) {
                onDelete(server.id);
              }
            }}
            title="Delete server"
          >
            <Trash2 className="w-4 h-4 text-red-600" />
          </Button>
        </div>
      </div>
    </Card>
  );
};
